import { getTextWidth } from "../../../helper/getTextWidth";
import { filterTestPoint } from "../../channel";
import { SERDES_TYPES } from "../../../PCBHelper";
import { getSignalGroup } from "./endToEnd";
import { COMINSERTION, COMRETURN, getReportGuideKeys, getReportGuideValue, INSERTIONLOSS, RETURNLOSS } from "../../../helper/reportHelper";
import { CPHY, PCIE } from "../../../PCBHelper/constants";

// Init Signal Ports
function initSignalPorts(ports, interfaceContent, isEndToEnd) {
  if (isEndToEnd) {
    return getEndToEndSignalPorts(interfaceContent);
  }
  let adsConfigSignals = [], channels = [];
  if (interfaceContent && interfaceContent.adsConfig && interfaceContent.adsConfig.signals) {
    adsConfigSignals = interfaceContent.adsConfig.signals;
  };
  if (interfaceContent && interfaceContent.config && interfaceContent.config.channels) {
    channels = interfaceContent.config.channels;
  };
  // [{signal, txPort: [{componentP, pinP, componentN, pinN}], rxPort: [{componentP, pinP, componentN, pinN}]}]
  const portData = [];
  const diffPorts = ports.filter(port => port.portType === 'Diff');
  const groupMap = new Map();
  const compMap = adsConfigSignals.length > 0 ? getAdsConfigComMap(adsConfigSignals)
    : channels.length > 0 ? getConfigComMap(channels)
      : getDefaultComMap(diffPorts);
  for (const port of diffPorts) {
    const signal = port.signal;
    const oldValue = groupMap.get(signal) || {};
    const txPort = oldValue.txPort || [],
      rxPort = oldValue.rxPort || [];
    const signalConfig = compMap.get(signal);
    const { controller } = signalConfig;
    if (controller === port.comp) {
      txPort.push({
        componentP: port.comp,
        pinP: port.positivePin,
        componentN: port.comp,
        pinN: port.negativePin
      })
    } else {
      rxPort.push({
        componentP: port.comp,
        pinP: port.positivePin,
        componentN: port.comp,
        pinN: port.negativePin
      })
    }
    groupMap.set(signal, {
      txPort,
      rxPort
    })
  }

  for (const [key, value] of groupMap) {
    portData.push({
      signal: key,
      txPort: value.txPort,
      rxPort: value.rxPort
    })
  }

  return portData;
}

function getAdsConfigComMap(adsConfigSignals) {
  const compMap = new Map();
  for (const signalConfig of adsConfigSignals) {
    const { signalName, rxAmiModel = {}, txAmiModel = {} } = signalConfig;
    compMap.set(signalName, { controller: rxAmiModel.component, device: txAmiModel.component });
  }

  return compMap;
}

function getConfigComMap(channels) {
  const compMap = new Map();
  for (const channel of channels) {
    const { signal, controller = {}, device = {} } = channel;
    compMap.set(signal, { controller: controller.component, device: device.component });
  }

  return compMap;
}

function getDefaultComMap(diffPorts) {
  const compMap = new Map();
  for (const port of diffPorts) {
    const { signal, comp } = port;
    const oldValue = compMap.get(signal) || {};
    if (!oldValue.controller) {
      compMap.set(signal, { controller: comp });
    } else {
      compMap.set(signal, { ...oldValue, device: comp })
    }
  }
  return compMap;
}

// Current signal RxPort -> Target Signal TxPort
function getResultObject(list = [], signalPorts = []) {
  const _list = JSON.parse(JSON.stringify(list));

  for (const item of _list) {
    const { children, rowName: currSignal } = item;
    const signalPort = signalPorts.find(port => port.signal === currSignal);
    const _children = children.filter(child => {
      if (signalPort.rxPort.find(it => it.componentP === child.comp)) {
        const targetSignalPort = signalPorts.find(port => port.signal === child._signal);
        if (targetSignalPort.txPort.find(it => it.componentP === child._comp)) {
          return true;
        }
      }
      return false;
    })
    item.children = _children;
  }

  return _list;
}

function getDisplaySignalPorts(signalPorts = [], components = [], isEndToEnd, pcbIndexMap, interfaceContent) {
  const portList = [];
  let leftWidth = 0, rightWidth = 0;
  for (const signalPort of signalPorts) {
    const { signal, txPort, rxPort } = signalPort;
    const portNum = txPort.length;
    const ports = [];
    for (const _index in (new Array(portNum).fill(0).map((_, i) => i))) {
      const _txPort = txPort[_index] || {}, _rxPort = rxPort[_index] || {};
      let txPCBIndex, rxPCBIndex, txComps = components, rxComps = components;
      if (isEndToEnd) {
        txPCBIndex = pcbIndexMap.get(_txPort.designName) || 0;
        rxPCBIndex = pcbIndexMap.get(_rxPort.designName) || 0;
        txComps = getMultiChannelComponents(interfaceContent, _txPort.channelId);
        rxComps = getMultiChannelComponents(interfaceContent, _rxPort.channelId);
      }
      let txCompIndex = txComps.findIndex(it => it.name === _txPort.componentP),
        rxCompIndex = rxComps.findIndex(it => it.name === _rxPort.componentP);

      // Confirm the location of the comp
      let isTxFirst = true;
      if (txCompIndex > rxCompIndex || (isEndToEnd && txPCBIndex > rxPCBIndex)) {
        isTxFirst = false;
      }
      let txPortText = `${_txPort.componentP}<+${_txPort.pinP}, -${_txPort.pinN}>` + (isEndToEnd ? ` PCB${txPCBIndex + 1}` : ''),
        rxPortText = `${_rxPort.componentP}<+${_rxPort.pinP}, -${_rxPort.pinN}>` + (isEndToEnd ? ` PCB${rxPCBIndex + 1}` : '');

      // Merge or copy
      if (judgeMergeOrCopy(txComps[txCompIndex])) {
        const txPortInfo = getNewPortInfo(txComps, { positivePin: _txPort.pinP, negativePin: _txPort.pinN }, txComps[txCompIndex]);
        txPortText = `${txPortInfo.comp}<+${txPortInfo.positivePin}, -${txPortInfo.negativePin}>` + (isEndToEnd ? ` PCB${txPCBIndex + 1}` : '');
      }

      if (judgeMergeOrCopy(rxComps[rxCompIndex])) {
        const rxPortInfo = getNewPortInfo(rxComps, { positivePin: _rxPort.pinP, negativePin: _rxPort.pinN }, rxComps[rxCompIndex]);
        rxPortText = `${rxPortInfo.comp}<+${rxPortInfo.positivePin}, -${rxPortInfo.negativePin}>` + (isEndToEnd ? ` PCB${rxPCBIndex + 1}` : '');
      }

      //width
      leftWidth = Math.max(getTextWidth(isTxFirst ? txPortText : rxPortText, 14), leftWidth);
      rightWidth = Math.max(getTextWidth(isTxFirst ? rxPortText : txPortText, 14), rightWidth);
      ports.push({
        leftPortText: isTxFirst ? txPortText : rxPortText,
        leftType: isTxFirst ? "TX" : "RX",
        rightPortText: isTxFirst ? rxPortText : txPortText,
        rightType: isTxFirst ? "RX" : "TX"
      })
    }
    portList.push({ signal, ports })
  }

  return { portList, leftWidth, rightWidth };
}

/**
 * Get the real port info
 * @param {*} components 
 * @param {*} param1
 * @param {Object} compInfo get layer
 * @param {string} pinsEleType "string" | "object"
 * @returns 
 */
function getNewPortInfo(components = [], { positivePin, negativePin }, compInfo, pinsEleType) {
  const pCompInfo = components.find(it => it.name === positivePin) || {};
  const nCompInfo = components.find(it => it.name === negativePin) || {};

  return {
    comp: [pCompInfo.name, nCompInfo.name].filter(it => !!it).join(",") + `(${compInfo.layer})`,
    positivePin: pCompInfo.pins && pCompInfo.pins[0] ? (pinsEleType === 'string' ? pCompInfo.pins[0] : pCompInfo.pins[0].pin) : "",
    negativePin: nCompInfo.pins && nCompInfo.pins[0] ? (pinsEleType === 'string' ? nCompInfo.pins[0] : nCompInfo.pins[0].pin) : ""
  }
}

function getMultiChannelComponents(interfaceContent = {}, channelId) {
  const channels = interfaceContent && interfaceContent.channels ? interfaceContent.channels : [];
  const channelInfo = channels.find(it => it.id === channelId);
  return channelInfo && channelInfo.content ? (channelInfo.content.components || []) : [];
}

function getSingleChannelComponents(interfaceContent = {}, isFilter = true) {
  if (interfaceContent && interfaceContent.content) {
    const { components, comp_merges } = interfaceContent.content;
    if (isFilter && comp_merges && comp_merges.length) {
      return filterTestPoint(components, comp_merges);
    }
    return components;
  }

  return [];
}

function judgeMergeOrCopy(compInfo = {}) {
  return compInfo.isMergeComp || compInfo.isCopyComp;
}

/**
 * 
 * @param {Object} item portInfo
 * @param {boolean} isEndToEnd 
 * @param {Object} interfaceContent 
 * @param {Map} historyContent
 * @param {Map} experimentContent
 * @returns 
 */
function getMergeCompDisplayPort(item, isEndToEnd, type, { channelContent, historyContent, experimentContent }) {
  // Check whether portInfo needs to be processed
  if (!judgeMergeOrCopy(item.compInfo || item.compInfo1) && !judgeMergeOrCopy(item._compInfo || item.compInfo2)) {
    return item
  }

  // Find content according to id
  const [fileId] = item.id.split('::');
  let interfaceContent;
  if (type === "History") {
    interfaceContent = historyContent.get(fileId);
  } else if (type === "experiments") {
    interfaceContent = experimentContent.get(fileId);
  } else {
    interfaceContent = channelContent;
  }

  if (!isEndToEnd) { // Single channel，item: {positivePin, negativePin, comp, compInfo, _positivePin, _negativePin, _comp, _compInfo}
    const components = getSingleChannelComponents(interfaceContent, false);
    let _item = { ...item };
    // comp
    if (judgeMergeOrCopy(item.compInfo)) {
      const newPortInfo = getNewPortInfo(components, { positivePin: item.positivePin, negativePin: item.negativePin }, item.compInfo);
      _item.comp = newPortInfo.comp;
      _item.positivePin = newPortInfo.positivePin;
      _item.negativePin = newPortInfo.negativePin;
    }

    //_comp
    if (judgeMergeOrCopy(item._compInfo)) {
      const newPortInfo = getNewPortInfo(components, { positivePin: item._positivePin, negativePin: item._negativePin }, item._compInfo);
      _item._comp = newPortInfo.comp;
      _item._positivePin = newPortInfo.positivePin;
      _item._negativePin = newPortInfo.negativePin;
    }

    return _item;
  } else { // Multi channel，item: {compInfo1, compInfo2, pins1, pins2, _design1CompPin, _design2CompPin}
    let _item = { ...item };
    const { compInfo1, compInfo2, pins1, pins2 } = item;
    // compInfo1
    if (judgeMergeOrCopy(compInfo1)) {
      const components = getMultiChannelComponents(interfaceContent, compInfo1.channelId);
      const newPortInfo = getNewPortInfo(components, { positivePin: pins1[0], negativePin: pins1[1] }, compInfo1);
      _item._design1CompPin = `${newPortInfo.comp}(+${newPortInfo.positivePin}, -${newPortInfo.negativePin})`;
      _item._comp1 = newPortInfo.comp;
    }

    // compInfo2
    if (judgeMergeOrCopy(compInfo2)) {
      const components = getMultiChannelComponents(interfaceContent, compInfo2.channelId);
      const newPortInfo = getNewPortInfo(components, { positivePin: pins2[0], negativePin: pins2[1] }, compInfo2);
      _item._design2CompPin = `${newPortInfo.comp}(+${newPortInfo.positivePin}, -${newPortInfo.negativePin})`;
      _item._comp2 = newPortInfo.comp;
    }

    // compInfo Default Type
    if (judgeMergeOrCopy(item.compInfo)) {
      const components = getMultiChannelComponents(interfaceContent, item.compInfo.channelId);
      const newPortInfo = getNewPortInfo(components, { positivePin: item.pins[0], negativePin: item.pins[1] }, item.compInfo);
      _item.comp = newPortInfo.comp;
      _item.pinsText = `<+${newPortInfo.positivePin}, -${newPortInfo.negativePin}>`;
    }
    return _item;
  }
}

function generateCurvesHashId(oldCurves, curves = []) {
  const hashIds = oldCurves.map(it => it.hashId).filter(id => id);

  curves.forEach((curve) => {
    //Generate 10-digit random numbers containing numbers and letters
    let str = Math.random().toString(36).substr(2, 12);
    //Determine if it already exists
    while (hashIds.includes(str)) {
      str = Math.random().toString(36).substr(2, 12);
    };
    hashIds.push(str);
    curve.hashId = str;
  })

  return curves;
}

function initSignalPortsByPowerSum(interfaceContent, isEndToEnd) {
  if (isEndToEnd) {
    return getEndToEndSignalPorts(interfaceContent);
  } else {
    const signals = interfaceContent && interfaceContent.content ? interfaceContent.content.signals || [] : [];
    const components = interfaceContent && interfaceContent.content ? interfaceContent.content.components || [] : [];
    const ICComp = components.filter(comp => SERDES_TYPES.includes(comp.type));

    // {portType, signal, comp, positivePin, negativePin}
    const ports = [];
    for (const comp of ICComp) {
      const { name, pins } = comp;
      for (const pin of pins) {
        const portIndex = ports.findIndex(port => port.comp === name && port.signal === pin.signal);
        const signal = signals.find(signal => signal.name === pin.signal);
        if (!signal) continue;
        const netType = [{ key: "nets_N", type: "negativePin" }, { key: "nets_P", type: "positivePin" }]
          .find(({ key }) => signal[key] && signal[key].includes(pin.net));
        if (!netType) continue;
        if (portIndex > -1) {
          ports[portIndex][netType.type] = pin.pin;
        } else {
          ports.push({
            portType: "Diff",
            signal: pin.signal,
            comp: name,
            [netType.type]: pin.pin
          })
        }
      }
    }
    const portData = initSignalPorts(ports, interfaceContent);

    return portData;
  }
}

function getPowerSumResultObject(list = [], signalPorts = [], isEndToEnd, displayMode, modeActive) {
  const _list = JSON.parse(JSON.stringify(list));

  // Filter out all Tx ports 
  for (const item of _list) {
    const { children, rowName: currSignal } = item;
    const signalPort = signalPorts.find(port => port.signal === currSignal) || {};
    if (!signalPort.rxPort) {
      continue;
    }
    const _children = children.filter(child => {
      if (signalPort.rxPort.find(it => it.componentP === child.component && (!isEndToEnd || it.designName === child.design))) {
        if (!["NEXT", "FEXT"].includes(displayMode) || (displayMode === 'FEXT' && modeActive === "SE_POWERSUM")) {
          return true;
        }
        const otherSignalPort = signalPorts.find(port => {
          if (port.signal !== child.signal) {
            return (port.txPort || []).find(it => (it.componentP === child.component && displayMode === "NEXT")
              || (it.componentP !== child.component && displayMode === "FEXT"))
          }
          return false;
        });

        return otherSignalPort;
      }
      return false;
    })
    item.children = _children;
  }
  return _list;
}

function getEndToEndSignalPorts(interfaceContent) {
  // let adsConfigSignals = [], channels = [];
  // if (interfaceContent.endToEnd && interfaceContent.endToEnd.adsConfig && interfaceContent.endToEnd.adsConfig.signals) {
  //   adsConfigSignals = this.interfaceContent.endToEnd.adsConfig.signals;
  // };
  // if (interfaceContent.endToEnd && interfaceContent.endToEnd.config && interfaceContent.endToEnd.config.analysis) {
  //   channels = interfaceContent.endToEnd.config.channels;
  // };

  const signalGroups = getSignalGroup(interfaceContent, true);
  const portData = [];
  for (const signalGroup of signalGroups) {
    const { beginChannel, endChannel } = signalGroup;
    portData.push({
      signal: signalGroup.name,
      txPort: [{
        componentP: beginChannel.components[0].name,
        pinP: beginChannel.components[0].pins[0],
        componentN: beginChannel.components[0].name,
        pinN: beginChannel.components[0].pins[1],
        designName: beginChannel.designName,
        channelId: beginChannel.channelId
      }],
      rxPort: [{
        componentP: endChannel.components[0].name,
        pinP: endChannel.components[0].pins[0],
        componentN: endChannel.components[0].name,
        pinN: endChannel.components[0].pins[1],
        designName: endChannel.designName,
        channelId: endChannel.channelId
      }]
    });
  }
  return portData;
}

function getDefaultGuideValue(key, dataRate, guideValue) {
  const _defaultGuideKey = getReportGuideKeys(`${key}_reference_line`);
  const _guideValue = guideValue && guideValue.length > 0 ? guideValue.filter((value) => _defaultGuideKey.includes(value.name)) : [];
  const defaultGuideValue = getReportGuideValue(key, _defaultGuideKey, dataRate);
  const nameList = []
  // Filter the qualified data
  let _referenceLineData = _guideValue.filter((item) => {
    if (_defaultGuideKey.includes(item.name)) {
      nameList.push(item.name)
      return true;
    }
    return false;
  })

  // Add the data that are not available
  defaultGuideValue.forEach((item) => {
    if (!nameList.includes(item.name)) {
      _referenceLineData.push(item)
    }
  })
  return _referenceLineData;
}

function getReferenceLinesOptionsData(referenceLinesList, interfaceType) {
  const tagMap = new Map();
  for (const item of referenceLinesList) {
    if (tagMap.has(item.tag)) {
      tagMap.set(item.tag, [...tagMap.get(item.tag), { label: item.name, value: item.id }])
    } else {
      tagMap.set(item.tag, [{ label: item.name, value: item.id }])
    }
  }

  const options = []
  tagMap.forEach((value, key) => {
    if (key === interfaceType) {
      options.unshift({ label: key, options: value })
    } else {
      options.push({ label: key, options: value })
    }
  })

  return options;
}

function importCustomConfig(oldCustom, newCustom, interfaceType) {

  let disabled = true, _isXTable = oldCustom.isXTable;
  const newGuideValue = oldCustom.guideValue.map((item) => {
    const newItem = newCustom.guideValue.find((it) => it.name === item.name);
    if (newItem) {
      const { points = [], x = [], y = [], driverPoints = [], receiverPoints = [], cphyInsertionLossSpec = "Short" } = newItem;
      if (x.length) {
        disabled = false;
      }
      if (interfaceType === CPHY) {
        return [INSERTIONLOSS, COMINSERTION].includes(newItem.name)
          ? { ...item, points, x, y, driverPoints, receiverPoints, cphyInsertionLossSpec }
          : { ...item, points, x, y, driverPoints, receiverPoints }
      }

      return [INSERTIONLOSS, RETURNLOSS, COMRETURN].includes()
        ? { ...item, points, x, y }
        : { ...item, points, x, y };
    }
    return item;
  })

  if (disabled && _isXTable) {
    _isXTable = false
  }
  let resCustom = {
    ...oldCustom,
    guideValue: newGuideValue,
    isXTable: _isXTable
  };

  if (interfaceType === CPHY) {
    const { symbolRate = 2.5, fh = 1.25, fMax = 0.1875, fINTMin = 0.45, fLPMax = 0.01 } = newCustom;
    resCustom = {
      ...resCustom,
      symbolRate,
      fh,
      fMax,
      fINTMin,
      fLPMax
    }
  } else if (interfaceType === PCIE) {
    const { dataRate = 16 } = newCustom;
    resCustom = {
      ...resCustom,
      dataRate
    }
  }

  return resCustom;
}

function exportCustomConfig(customConfig, guideKey, interfaceType) {
  const { guideValue = [], symbolRate = 2.5, fh, fMax, fINTMin = 0.45, fLPMax = 0.01, dataRate = 16 } = customConfig;
  let _guideValue = guideValue
    .filter((item) => guideKey.includes(item.name))
    .map((item) => {
      const { name, points, x, y, driverPoints, receiverPoints, cphyInsertionLossSpec } = item;
      const defaultRes = interfaceType === CPHY ? { name, points, x, y, driverPoints, receiverPoints } : { name, points, x, y };
      if (interfaceType === CPHY && [INSERTIONLOSS, COMINSERTION].includes(item.name)) {
        return { ...defaultRes, cphyInsertionLossSpec }
      }
      if (interfaceType !== CPHY && [INSERTIONLOSS, RETURNLOSS, COMRETURN].includes(item.name)) {
        return { ...defaultRes }
      }
      return defaultRes;
    });

  if (interfaceType === CPHY) {
    return { guideValue: _guideValue, symbolRate, fh, fMax, fINTMin, fLPMax };
  } else if (interfaceType === PCIE) {
    return { guideValue: _guideValue, dataRate };
  }
  return { guideValue: _guideValue };
}

export {
  initSignalPorts,
  getResultObject,
  getDisplaySignalPorts,
  getSingleChannelComponents,
  getMultiChannelComponents,
  getMergeCompDisplayPort,
  getNewPortInfo,
  judgeMergeOrCopy,
  initSignalPortsByPowerSum,
  getPowerSumResultObject,
  generateCurvesHashId,
  getDefaultGuideValue,
  getReferenceLinesOptionsData,
  importCustomConfig,
  exportCustomConfig
}